// AgentDigest.jsx — Phase 4F
// Per-agent monthly performance digest with AI improvement tips.

import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid,
} from 'recharts';
import {
  Loader2, Phone, BookOpen, Trophy, DollarSign, CheckCircle2,
  TrendingUp, AlertTriangle, ChevronLeft, Printer, Calendar,
  BarChart3, Brain,
} from 'lucide-react';
import { apiGet } from '../lib/api.js';
import { useAuth } from '../store/auth.js';
import TopBar from '../components/TopBar.jsx';

const MONTH_OPTIONS = (() => {
  const opts = [];
  const now = new Date();
  for (let i = 0; i < 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const val = d.toISOString().slice(0, 7);
    const label = d.toLocaleString('default', { month: 'long', year: 'numeric' });
    opts.push({ value: val, label });
  }
  return opts;
})();

function StatCard({ icon: Icon, label, value, sub, color = 'text-civix-teal' }) {
  return (
    <div className="panel p-5 flex items-start gap-4">
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${color === 'text-civix-teal' ? 'bg-civix-teal/10' : color === 'text-civix-purple' ? 'bg-civix-purple/10' : 'bg-yellow-400/10'} shrink-0`}>
        <Icon className={`w-5 h-5 ${color}`} />
      </div>
      <div>
        <div className="text-2xl font-bold text-white">{value}</div>
        <div className="text-xs text-white/50">{label}</div>
        {sub && <div className="text-xs text-white/30 mt-0.5">{sub}</div>}
      </div>
    </div>
  );
}

export default function AgentDigest() {
  const { id } = useParams();
  const nav = useNavigate();
  const { agent: me, isAdmin } = useAuth();

  // Use current agent's ID if not provided
  const agentId = id || String(me?._id || '');
  const [month, setMonth] = useState(MONTH_OPTIONS[0]?.value || '');

  const { data, isLoading, isError } = useQuery({
    queryKey: ['agent-digest', agentId, month],
    queryFn: () => apiGet(`/agents/${agentId}/monthly-digest`, { month }),
    enabled: !!agentId && !!month,
    staleTime: 120000,
  });

  const digest = data?.data || data || null;

  const monthLabel = MONTH_OPTIONS.find((o) => o.value === month)?.label || month;

  return (
    <div className="space-y-6" id="digest-print-area">
      <TopBar
        title="Monthly Digest"
        subtitle={digest ? `${digest.agent?.name} · ${monthLabel}` : 'Performance Summary'}
        actions={
          <div className="flex items-center gap-3">
            <select
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              className="input text-sm py-1.5 px-3"
            >
              {MONTH_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
            {isAdmin() && (
              <button onClick={() => nav(-1)} className="btn-ghost text-sm flex items-center gap-1">
                <ChevronLeft className="w-4 h-4" /> Back
              </button>
            )}
            <button
              onClick={() => window.print()}
              className="btn-ghost text-sm flex items-center gap-2"
            >
              <Printer className="w-4 h-4" /> Print / Save PDF
            </button>
          </div>
        }
      />

      {isLoading && (
        <div className="flex justify-center py-16">
          <Loader2 className="w-8 h-8 text-civix-purple animate-spin" />
        </div>
      )}

      {isError && (
        <div className="panel p-8 text-center">
          <AlertTriangle className="w-10 h-10 text-yellow-400 mx-auto mb-3" />
          <p className="text-white/70">Could not load digest. Please try again.</p>
        </div>
      )}

      {!isLoading && digest && (
        <>
          {/* Hero */}
          <div className="panel p-6 bg-linear-to-br from-civix-purple/10 to-civix-teal/5 border border-civix-purple/20">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-2xl bg-civix-gradient flex items-center justify-center text-2xl font-bold">
                {digest.agent?.name?.[0] || '?'}
              </div>
              <div>
                <h2 className="text-2xl font-display font-bold text-white">{digest.agent?.name}</h2>
                <p className="text-white/50 text-sm">{monthLabel} Performance Summary</p>
              </div>
              <div className="ml-auto flex items-center gap-2 bg-civix-purple/20 border border-civix-purple/40 rounded-xl px-4 py-2">
                <Trophy className="w-5 h-5 text-yellow-400" />
                <span className="text-lg font-bold text-white">#{digest.leaderboardRank}</span>
                <span className="text-xs text-white/50">Leaderboard</span>
              </div>
            </div>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
            <StatCard icon={Phone}     label="Calls Made"       value={digest.callsMade}       color="text-civix-teal" />
            <StatCard icon={BookOpen}  label="Bookings"         value={digest.callsBooked}      color="text-civix-cyan" />
            <StatCard icon={TrendingUp} label="Conversion"      value={`${digest.conversionRate}%`} color="text-civix-purple" />
            <StatCard icon={Brain}     label="Roleplays"        value={digest.roleplays?.count} sub={`Avg ${digest.roleplays?.avgScore || 0}/10`} color="text-yellow-400" />
            <StatCard icon={DollarSign} label="AI Spend"        value={`$${digest.aiCostUsd?.toFixed(2)}`} color="text-white/60" />
          </div>

          <div className="grid lg:grid-cols-2 gap-4">
            {/* Highlights */}
            <div className="panel p-5 space-y-3">
              <div className="flex items-center gap-2 mb-1">
                <CheckCircle2 className="w-4 h-4 text-green-400" />
                <h3 className="font-semibold text-white">Highlights</h3>
              </div>
              {digest.highlights?.length > 0 ? (
                <ul className="space-y-2">
                  {digest.highlights.map((h, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-white/80">
                      <CheckCircle2 className="w-4 h-4 text-green-400 shrink-0 mt-0.5" />
                      {h}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-white/40 text-sm">No highlights recorded this month.</p>
              )}
            </div>

            {/* Improvement Tips */}
            <div className="panel p-5 space-y-3">
              <div className="flex items-center gap-2 mb-1">
                <AlertTriangle className="w-4 h-4 text-yellow-400" />
                <h3 className="font-semibold text-white">Improvement Tips</h3>
              </div>
              {digest.improvementTips?.length > 0 ? (
                <ul className="space-y-2">
                  {digest.improvementTips.slice(0, 3).map((tip, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-white/80">
                      <span className="w-5 h-5 rounded-full bg-yellow-400/20 text-yellow-400 text-xs flex items-center justify-center shrink-0 mt-0.5 font-bold">{i + 1}</span>
                      {tip}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-white/40 text-sm">Keep practicing — tips will appear after roleplay sessions.</p>
              )}
            </div>
          </div>

          {/* Roleplay Progress Chart */}
          {digest.roleplays?.weeklyScores?.length > 0 && (
            <div className="panel p-5">
              <div className="flex items-center gap-2 mb-4">
                <BarChart3 className="w-4 h-4 text-civix-purple" />
                <h3 className="font-semibold text-white">Roleplay Progress</h3>
                <span className="text-xs text-white/40 ml-auto">Weekly avg score (out of 10)</span>
              </div>
              <ResponsiveContainer width="100%" height={180}>
                <LineChart data={digest.roleplays.weeklyScores} margin={{ top: 5, right: 10, left: -20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                  <XAxis dataKey="week" tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 11 }} axisLine={false} tickLine={false} />
                  <YAxis domain={[0, 10]} tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 11 }} axisLine={false} tickLine={false} />
                  <Tooltip
                    contentStyle={{ background: '#1a1a2e', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8 }}
                    labelStyle={{ color: 'rgba(255,255,255,0.6)' }}
                    itemStyle={{ color: '#a78bfa' }}
                  />
                  <Line type="monotone" dataKey="avg" stroke="#a78bfa" strokeWidth={2} dot={{ fill: '#a78bfa', strokeWidth: 0, r: 4 }} activeDot={{ r: 6 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Roleplay details */}
          {digest.roleplays && (
            <div className="panel p-5">
              <div className="flex items-center gap-2 mb-4">
                <Brain className="w-4 h-4 text-civix-purple" />
                <h3 className="font-semibold text-white">Roleplay Details</h3>
              </div>
              <div className="grid grid-cols-3 gap-4">
                {[
                  { label: 'Sessions', value: digest.roleplays.count },
                  { label: 'Avg Score', value: `${digest.roleplays.avgScore || 0} / 10` },
                  { label: 'Best Score', value: `${digest.roleplays.bestScore || 0} / 10` },
                ].map(({ label, value }) => (
                  <div key={label} className="bg-civix-panelLight rounded-lg p-3 text-center">
                    <div className="text-xl font-bold text-white">{value}</div>
                    <div className="text-xs text-white/40 mt-1">{label}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
